/**
 * TerrainRenderPass.ts — renders the static terrain + lakebed mesh.
 *
 * Geometry: a regular vertex grid built procedurally in the vertex shader from
 * the vertex_index. We only allocate an index buffer (uint32). Stage 2 will
 * replace this with CDLOD when the camera moves close to the shore.
 *
 * Reads:  Camera, Lighting, Palette, Terrain (uniform), heightmap texture.
 * Writes: RenderTarget.color (load), RenderTarget.depth (load, reverse-Z compare).
 *
 * Defaults: 512 quads/side over 2 km extent → quad ≈ 3.9 m (CDLOD comes in stage 2).
 */

import terrainWgsl from "../shaders/terrain.wgsl?raw";
import type { Camera } from "../Camera";
import type { LightingUniforms } from "../rendering/LightingUniforms";
import type { PaletteUniforms } from "../rendering/PaletteUniforms";
import type { ManagedRenderTarget } from "../rendering/RenderTarget";
import { REVERSE_Z_COMPARE } from "../rendering/RenderTarget";
import type { FrameContext, RenderPass } from "../rendering/RenderPass";
import {
  generateProceduralTerrain,
  uploadHeightmapTexture,
  type TerrainHeightmap,
} from "../data/procedural-terrain";
import {
  createBufferFromTypedArray,
  createUniformBuffer,
} from "../gpu/Buffers";

export interface TerrainPassOptions {
  /** Quads per side (vertices per side = quads + 1). Default 512. */
  quads?: number;
  /** Override generated heightmap. */
  heightmap?: TerrainHeightmap;
  /** Initial water level (metres). Default 0. */
  waterLevel?: number;
  /**
   * Optional terrain classification raster — single byte per texel encoding
   * `TerrainSurface` enum values. Same UV layout as the heightmap.
   * If omitted, an all-zeros 1×1 texture is used and the shader treats it
   * as "no info".
   */
  classification?: {
    data: Uint8Array;
    width: number;
    height: number;
  };
  /** Smooth road coverage texture: R=road. */
  roadCoverage?: {
    data: Uint8Array;
    width: number;
    height: number;
  };
  /**
   * R8 polygon-edge fade mask. 0 = outside polygon (fragment discarded),
   * 1 = fully inside, smooth gradient near the boundary. Same UV layout as
   * the classification texture.
   */
  polygonFade?: {
    data: Uint8Array;
    width: number;
    height: number;
  };
}

export class TerrainRenderPass implements RenderPass {
  readonly name = "TerrainRenderPass";

  private pipeline: GPURenderPipeline;
  private bgl: GPUBindGroupLayout;
  private bindGroup: GPUBindGroup;
  private indexBuffer: GPUBuffer;
  private indexCount: number;
  private terrainUniform: GPUBuffer;
  private heightmapTex: GPUTexture;
  private classificationTex: GPUTexture;
  private roadCoverageTex: GPUTexture;
  private polygonFadeTex: GPUTexture;
  readonly heightmap: TerrainHeightmap;

  /** Public so UI / state can update without reaching through privates. */
  waterLevel: number;

  /** Shadow bind group (group 1). Set externally before encode(). */
  shadowBindGroup: GPUBindGroup | null = null;

  constructor(
    device: GPUDevice,
    rt: ManagedRenderTarget,
    camera: Camera,
    lighting: LightingUniforms,
    palette: PaletteUniforms,
    shadowConsumerLayout: GPUBindGroupLayout,
    opts: TerrainPassOptions = {},
  ) {
    const quads = opts.quads ?? 512;
    this.heightmap = opts.heightmap ?? generateProceduralTerrain();
    this.waterLevel = opts.waterLevel ?? 0;

    this.heightmapTex = uploadHeightmapTexture(device, this.heightmap);

    // Classification raster — `r8uint` storing TerrainSurface enum.
    const cls = opts.classification;
    if (cls) {
      const clsData = new Uint8Array(cls.data);
      this.classificationTex = device.createTexture({
        label: "TerrainRenderPass.classification",
        size: { width: cls.width, height: cls.height },
        format: "r8uint",
        usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
      });
      device.queue.writeTexture(
        { texture: this.classificationTex },
        clsData,
        { bytesPerRow: cls.width, rowsPerImage: cls.height },
        { width: cls.width, height: cls.height },
      );
    } else {
      // Dummy 1×1 zero-filled texture so the bind group always has a view.
      this.classificationTex = device.createTexture({
        label: "TerrainRenderPass.classification.dummy",
        size: { width: 1, height: 1 },
        format: "r8uint",
        usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
      });
      device.queue.writeTexture(
        { texture: this.classificationTex },
        new Uint8Array([0]),
        { bytesPerRow: 1, rowsPerImage: 1 },
        { width: 1, height: 1 },
      );
    }

    const road = opts.roadCoverage;
    if (road) {
      this.roadCoverageTex = device.createTexture({
        label: "TerrainRenderPass.roadCoverage",
        size: { width: road.width, height: road.height },
        format: "rgba8unorm",
        usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
      });
      device.queue.writeTexture(
        { texture: this.roadCoverageTex },
        new Uint8Array(road.data),
        { bytesPerRow: road.width * 4, rowsPerImage: road.height },
        { width: road.width, height: road.height },
      );
    } else {
      this.roadCoverageTex = device.createTexture({
        label: "TerrainRenderPass.roadCoverage.dummy",
        size: { width: 1, height: 1 },
        format: "rgba8unorm",
        usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
      });
      device.queue.writeTexture(
        { texture: this.roadCoverageTex },
        new Uint8Array([0, 0, 0, 0]),
        { bytesPerRow: 4, rowsPerImage: 1 },
        { width: 1, height: 1 },
      );
    }

    const fade = opts.polygonFade;
    if (fade) {
      this.polygonFadeTex = device.createTexture({
        label: "TerrainRenderPass.polygonFade",
        size: { width: fade.width, height: fade.height },
        format: "r8unorm",
        usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
      });
      device.queue.writeTexture(
        { texture: this.polygonFadeTex },
        new Uint8Array(fade.data),
        { bytesPerRow: fade.width, rowsPerImage: fade.height },
        { width: fade.width, height: fade.height },
      );
    } else {
      // No fade authored — render fully opaque everywhere (255).
      this.polygonFadeTex = device.createTexture({
        label: "TerrainRenderPass.polygonFade.dummy",
        size: { width: 1, height: 1 },
        format: "r8unorm",
        usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
      });
      device.queue.writeTexture(
        { texture: this.polygonFadeTex },
        new Uint8Array([255]),
        { bytesPerRow: 1, rowsPerImage: 1 },
        { width: 1, height: 1 },
      );
    }

    // Build index buffer for (quads × quads) tiles.
    const vps = quads + 1;
    const indices = new Uint32Array(quads * quads * 6);
    let k = 0;
    for (let j = 0; j < quads; j++) {
      for (let i = 0; i < quads; i++) {
        const a = j * vps + i;
        const b = a + 1;
        const c = a + vps;
        const d = c + 1;
        indices[k++] = a;
        indices[k++] = c;
        indices[k++] = b;
        indices[k++] = b;
        indices[k++] = c;
        indices[k++] = d;
      }
    }
    this.indexCount = indices.length;
    this.indexBuffer = createBufferFromTypedArray(
      device,
      indices,
      GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST,
      "TerrainRenderPass.index",
    );

    // Terrain uniform: extent, res, quads, waterLevel (16 bytes).
    this.terrainUniform = createUniformBuffer(
      device,
      16,
      "TerrainRenderPass.terrainUniform",
    );
    this.uploadTerrainUniform(device, quads);

    // Pipeline & bind group.
    const module = device.createShaderModule({
      label: "terrain.wgsl",
      code: terrainWgsl,
    });
    this.bgl = device.createBindGroupLayout({
      label: "TerrainRenderPass.bgl",
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 2,
          visibility: GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 3,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 4,
          visibility: GPUShaderStage.VERTEX,
          texture: { sampleType: "unfilterable-float" },
        },
        {
          binding: 5,
          visibility: GPUShaderStage.FRAGMENT,
          texture: { sampleType: "uint" },
        },
        {
          binding: 6,
          visibility: GPUShaderStage.FRAGMENT,
          texture: { sampleType: "float" },
        },
        {
          binding: 7,
          visibility: GPUShaderStage.FRAGMENT,
          texture: { sampleType: "float" },
        },
      ],
    });
    this.pipeline = device.createRenderPipeline({
      label: "TerrainRenderPass.pipeline",
      layout: device.createPipelineLayout({
        bindGroupLayouts: [this.bgl, shadowConsumerLayout],
      }),
      vertex: { module, entryPoint: "vs_main" },
      fragment: {
        module,
        entryPoint: "fs_main",
        targets: [{ format: rt.current.colorFormat }],
      },
      primitive: {
        topology: "triangle-list",
        cullMode: "none",
      },
      depthStencil: {
        format: rt.current.depthFormat,
        depthWriteEnabled: true,
        depthCompare: REVERSE_Z_COMPARE,
      },
    });

    this.bindGroup = device.createBindGroup({
      label: "TerrainRenderPass.bg",
      layout: this.bgl,
      entries: [
        { binding: 0, resource: { buffer: camera.buffer } },
        { binding: 1, resource: { buffer: lighting.buffer } },
        { binding: 2, resource: { buffer: palette.buffer } },
        { binding: 3, resource: { buffer: this.terrainUniform } },
        { binding: 4, resource: this.heightmapTex.createView() },
        { binding: 5, resource: this.classificationTex.createView() },
        { binding: 6, resource: this.roadCoverageTex.createView() },
        { binding: 7, resource: this.polygonFadeTex.createView() },
      ],
    });
  }

  private uploadTerrainUniform(device: GPUDevice, quads: number): void {
    const data = new Float32Array([
      this.heightmap.extent,
      this.heightmap.resolution,
      quads,
      this.waterLevel,
    ]);
    device.queue.writeBuffer(this.terrainUniform, 0, data);
  }

  /** Update water level (UI-driven). */
  setWaterLevel(device: GPUDevice, waterLevel: number): void {
    this.waterLevel = waterLevel;
    // Only the last float changed; rewrite full uniform for simplicity.
    const quads = Math.round(Math.sqrt(this.indexCount / 6));
    this.uploadTerrainUniform(device, quads);
  }

  /** Index buffer accessor (used by ShadowPass to re-render the same mesh). */
  get indexBufferRef(): GPUBuffer {
    return this.indexBuffer;
  }
  get indexCountRef(): number {
    return this.indexCount;
  }
  /** Terrain uniform buffer (extent/res/quads/waterLevel) for shader reuse. */
  get terrainUniformBuffer(): GPUBuffer {
    return this.terrainUniform;
  }

  encode(encoder: GPUCommandEncoder, frame: FrameContext): void {
    const pass = encoder.beginRenderPass({
      label: "TerrainRenderPass",
      colorAttachments: [
        {
          view: frame.renderTarget.colorView,
          loadOp: "load",
          storeOp: "store",
        },
      ],
      depthStencilAttachment: {
        view: frame.renderTarget.depthView,
        depthLoadOp: "load",
        depthStoreOp: "store",
      },
    });
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bindGroup);
    if (this.shadowBindGroup !== null) {
      pass.setBindGroup(1, this.shadowBindGroup);
    }
    pass.setIndexBuffer(this.indexBuffer, "uint32");
    pass.drawIndexed(this.indexCount, 1, 0, 0, 0);
    pass.end();
  }

  destroy(): void {
    this.indexBuffer.destroy();
    this.terrainUniform.destroy();
    this.heightmapTex.destroy();
  }

  /** Sampled view of the heightmap texture (used by WaterRenderPass for depth). */
  heightmapView(): GPUTextureView {
    return this.heightmapTex.createView({ label: "Terrain.heightmap.view" });
  }

  /** Sampled view of the classification texture (used by WaterRenderPass to
   *  discard fragments outside the baked polygon area). */
  classificationView(): GPUTextureView {
    return this.classificationTex.createView({
      label: "Terrain.classification.view",
    });
  }

  /** Sampled view of the polygon-edge fade mask (used by WaterRenderPass to
   *  softly fade water alpha at the polygon boundary). */
  polygonFadeView(): GPUTextureView {
    return this.polygonFadeTex.createView({
      label: "Terrain.polygonFade.view",
    });
  }
}
